<script lang="ts">
  import { onMount } from 'svelte';
  import { appState } from './stores/app';
  import { notifications } from './stores/notifications';
  import { authStore, isAuthenticated, currentUser, authInitialized, authLoading } from './stores/auth';
  import { apiService } from './services/api';
  import FileUpload from './components/FileUpload.svelte';
  import ImagePreview from './ImagePreview.svelte';
  import MetadataTree from './components/MetadataTree.svelte';
  import ResultsDashboard from './components/ResultsDashboard.svelte';
  import ChatMapping from './components/ChatMapping.svelte';
  import NotificationContainer from './components/NotificationContainer.svelte';

  let processingStage: 'idle' | 'uploading' | 'generating' = 'idle';

  // LLM model selection
  let availableModels: string[] = [];
  let selectedModel = '';
  let modelsLoading = false;

  // User-provided instrument, experiment, and NeXus definition
  let userInstrument = '';
  let userExperiment = '';
  let userNexusDefinition = '';

  const EXPERIMENT_TO_NX: Record<string, string> = {
    SEM: 'NXem',
    TEM: 'NXem',
    STEM: 'NXem',
    AFM: 'NXafm',
    XPS: 'NXxps',
    XAS: 'NXxas',
    ARPES: 'NXarpes',
    Raman: 'NXraman',
    Ellipsometry: 'NXellipsometry',
    STXM: 'NXstxm',
    Tomography: 'NXtomo',
    MX: 'NXmx',
    APT: 'NXapm',
    Transport: 'NXiv_temp',
    Other: 'NXem',
  };

  $: if (userExperiment && EXPERIMENT_TO_NX[userExperiment]) {
    userNexusDefinition = EXPERIMENT_TO_NX[userExperiment];
  }

  // Mapping state for chat updates
  let currentMapping: Record<string, string> = {};
  let changedKeys: string[] = [];
  let removedKeys: { key: string; oldPath: string }[] = [];

  async function fetchAvailableModels() {
    modelsLoading = true;
    try {
      const result = await apiService.getAvailableModels();
      availableModels = result.models;
      if (result.default_model && result.models.includes(result.default_model)) {
        selectedModel = result.default_model;
      } else if (result.models.length > 0) {
        selectedModel = result.models[0];
      }
    } catch (e) {
      console.warn('Failed to fetch available models:', e);
    } finally {
      modelsLoading = false;
    }
  }

  // Initialize auth on mount
  onMount(() => {
    authStore.initialize();

    // Check for auth error in URL (from failed OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get('auth_error');
    if (authError) {
      notifications.error(`Authentication failed: ${authError}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  });

  // Fetch models when user becomes authenticated
  $: if ($isAuthenticated) {
    fetchAvailableModels();
  }

  function handleLogin() {
    authStore.login();
  }

  async function handleLogout() {
    await authStore.logout();
    appState.reset();
    notifications.info('Logged out successfully');
  }

  async function handleFileSelected({ file }: { file: File }) {
    appState.setFile(file);
    notifications.info(`Selected file: ${file.name}`);
  }

  async function handleUpload() {
    if (!$appState.file) return;

    processingStage = 'uploading';
    appState.setLoading(true);
    appState.setError(null);

    try {
      const result = await apiService.uploadImage($appState.file);
      appState.setFileId(result.file_id);
      appState.setMetadata(result.metadata);
      notifications.success(`File uploaded successfully! Extracted ${Object.keys(result.metadata).length} metadata fields.`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      appState.setError(errorMessage);
      notifications.error(errorMessage);
    } finally {
      processingStage = 'idle';
      appState.setLoading(false);
    }
  }

  async function handleGenerateMetadata() {
    if (!$appState.fileId) return;

    processingStage = 'generating';
    appState.setLoading(true);
    appState.setError(null);

    try {
      const result = await apiService.generateMetadata($appState.fileId, {
        instrument: userInstrument,
        experiment: userExperiment,
        nexus_definition: userNexusDefinition,
        model: selectedModel,
      });
      appState.setNexusResult(result);
      // Initialize currentMapping with the result mapping
      currentMapping = { ...result.metadata_mapping };
      changedKeys = [];
      notifications.success('NeXus metadata generated successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Generation failed';
      appState.setError(errorMessage);
      notifications.error(errorMessage);
    } finally {
      processingStage = 'idle';
      appState.setLoading(false);
    }
  }

  function handleReset() {
    appState.reset();
    notifications.clear();
    processingStage = 'idle';
    currentMapping = {};
    changedKeys = [];
    removedKeys = [];
    userInstrument = '';
    userExperiment = '';
    userNexusDefinition = '';
    // Re-fetch models (don't reset selectedModel, keep the user's choice)
  }

  function handleMappingUpdate({ updatedMapping, changedKeys: newChangedKeys, removedKeys: newRemovedKeys, nexusId }: {
    updatedMapping: Record<string, string>;
    changedKeys: string[];
    removedKeys: { key: string; oldPath: string }[];
    nexusId: string;
  }) {

    // Update mapping state
    currentMapping = updatedMapping;
    changedKeys = newChangedKeys;
    removedKeys = newRemovedKeys;

    // Update nexus_id if a new file was generated
    if (nexusId && $appState.nexusResult) {
      appState.setNexusResult({
        ...$appState.nexusResult,
        nexus_id: nexusId
      });
    }

    // Clear highlights after 10 seconds
    setTimeout(() => {
      changedKeys = [];
      removedKeys = [];
    }, 10000);
  }

   $: hasFile = !!$appState.file;
   $: hasMetadata = Object.keys($appState.metadata).length > 0;
   $: hasResults = !!$appState.nexusResult;
   $: canUpload = hasFile && processingStage === 'idle' && !$appState.loading;
   $: canGenerate = $appState.fileId && hasMetadata && processingStage === 'idle' && !$appState.loading;
   $: fileExtension = $appState.file?.name.split('.').pop()?.toLowerCase();
   $: isImageFile = $appState.file && ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'gif', 'bmp', 'webp'].includes(fileExtension || '');
   $: showImagePreview = $appState.fileId && isImageFile;
   $: previewUrl = $appState.fileId && isImageFile && $appState.file ? URL.createObjectURL($appState.file) : null;
</script>

<div class="app-container">
  <!-- Header -->
  <header class="app-header">
    <div class="header-content">
      <div class="header-title">
        <h1 class="title">FAIR NeXus Metadata Generator</h1>
        <p class="subtitle">Convert microscopy images to FAIR-compliant NeXus files</p>
      </div>
      <div class="header-actions">
        {#if hasFile && $isAuthenticated}
          <button
            on:click={handleReset}
            class="reset-button"
          >
            Start Over
          </button>
        {/if}
        {#if $authInitialized}
          {#if $isAuthenticated}
            <div class="user-info">
              <span class="user-name">{$currentUser?.name || $currentUser?.email || 'User'}</span>
              <button
                on:click={handleLogout}
                class="logout-button"
                disabled={$authLoading}
              >
                Log Out
              </button>
            </div>
          {:else}
            <button
              on:click={handleLogin}
              class="login-button"
              disabled={$authLoading}
            >
              {#if $authLoading}
                <span class="spinner-small"></span>
              {/if}
              Log In
            </button>
          {/if}
        {:else}
          <span class="loading-text">Loading...</span>
        {/if}
      </div>
    </div>
  </header>

  <!-- Main Content -->
  <main class="app-main">
    {#if !$authInitialized}
      <!-- Loading state while checking auth -->
      <section class="loading-section">
        <div class="loading-card">
          <span class="spinner-large"></span>
          <p>Checking authentication...</p>
        </div>
      </section>
    {:else if !$isAuthenticated}
      <!-- Login prompt for unauthenticated users -->
      <section class="login-prompt-section">
        <div class="login-prompt-card">
          <div class="login-prompt-icon">🔐</div>
          <h2 class="login-prompt-title">Authentication Required</h2>
          <p class="login-prompt-message">
            Please log in to access the FAIR NeXus Metadata Generator.
          </p>
          <button
            on:click={handleLogin}
            class="primary-button login-prompt-button"
            disabled={$authLoading}
          >
            {#if $authLoading}
              <span class="spinner"></span>
            {/if}
            Log In to Continue
          </button>
        </div>
      </section>
    {:else}
      <!-- Project intro -->
      <section class="intro-section">
        <div class="intro-logos">
          <img src="/logodualistic.svg" alt="Dualistic logo" class="intro-logo" />
          <img src="/area-science-park.jpeg" alt="Area Science Park logo" class="intro-logo" />
        </div>
        <div class="intro-text">
          <h2 class="intro-title">Welcome to FAIRAgent</h2>
          <p class="intro-description">
            FAIRAgent is a tool that turns raw microscopy data into FAIR-compliant
            NeXus files. Upload an image, and the agent extracts its metadata, maps it
            to the appropriate NeXus definition, and lets you refine the result through
            an interactive chat, making your experimental data Findable, Accessible,
            Interoperable, and Reusable.
          </p>
        </div>
      </section>

      <!-- File Upload Section (authenticated) -->
      <section class="upload-section">
        <FileUpload
          file={$appState.file}
          fileId={$appState.fileId}
          uploading={$appState.loading}
          onfileSelected={handleFileSelected}
        />

        {#if hasFile}
          <!-- User inputs for instrument, experiment, and NeXus definition -->
          <div class="nexus-inputs">
            <h3 class="nexus-inputs-title">NeXus Configuration</h3>
            <div class="nexus-inputs-grid">
              <div class="input-group">
                <label for="instrument-input" class="input-label">Instrument</label>
                <input
                  id="instrument-input"
                  type="text"
                  bind:value={userInstrument}
                  placeholder="e.g. Zeiss Gemini 500"
                  class="text-input"
                />
              </div>
              <div class="input-group">
                <label for="experiment-select" class="input-label">Experiment Type</label>
                <select
                  id="experiment-select"
                  bind:value={userExperiment}
                  class="select-input"
                >
                  <option value="">Select experiment type...</option>
                  {#each Object.keys(EXPERIMENT_TO_NX) as exp}
                    <option value={exp}>{exp}</option>
                  {/each}
                </select>
              </div>
              <div class="input-group">
                <label for="nexus-def-input" class="input-label">NeXus Definition</label>
                <input
                  id="nexus-def-input"
                  type="text"
                  bind:value={userNexusDefinition}
                  placeholder="e.g. NXem"
                  class="text-input"
                />
              </div>
              <div class="input-group">
                <label for="model-select" class="input-label">LLM Model</label>
                {#if modelsLoading}
                  <div class="text-sm text-gray-500">Loading models...</div>
                {:else if availableModels.length > 0}
                  <select
                    id="model-select"
                    bind:value={selectedModel}
                    class="select-input"
                  >
                    {#each availableModels as model}
                      <option value={model}>{model}</option>
                    {/each}
                  </select>
                {:else}
                  <div class="text-sm text-gray-400">No models available</div>
                {/if}
              </div>
            </div>
          </div>

          <div class="upload-actions">
            <button
              on:click={handleUpload}
              disabled={!canUpload}
              class="primary-button"
            >
              {#if processingStage === 'uploading'}
                <span class="spinner"></span>
                Uploading and extracting metadata...
              {:else}
                Upload & Extract Metadata
              {/if}
            </button>
          </div>
        {/if}
      </section>

      <!-- Image Preview -->
      {#if showImagePreview && previewUrl}
        <section class="preview-section">
          <div class="preview-container">
            <h2 class="preview-title">Image Preview</h2>
            <ImagePreview imageUrl={previewUrl} alt="Uploaded microscopy image" file={$appState.file} />
          </div>
        </section>
      {/if}

      <!-- Error Display -->
      {#if $appState.error}
        <section class="error-section">
          <div class="error-card">
            <div class="error-icon">⚠️</div>
            <div class="error-content">
              <h3 class="error-title">Error</h3>
              <p class="error-message">{$appState.error}</p>
            </div>
          </div>
        </section>
      {/if}

      <!-- Metadata Display -->
      {#if hasMetadata}
        <section class="metadata-section">
          <div class="section-header">
            <h2 class="section-title">Extracted Metadata</h2>
          </div>

          <MetadataTree data={$appState.metadata} />

          {#if canGenerate}
            <div class="generate-action">
              <button
                on:click={handleGenerateMetadata}
                disabled={!canGenerate}
                class="secondary-button"
              >
                {#if processingStage === 'generating'}
                  <span class="spinner"></span>
                  Generating NeXus metadata...
                {:else}
                  Generate NeXus Metadata
                {/if}
              </button>
            </div>
          {/if}
        </section>
      {/if}

      <!-- Results Display -->
      {#if hasResults}
        <section class="results-section">
          <ResultsDashboard
            result={$appState.nexusResult!}
            metadata={$appState.metadata}
            currentMapping={currentMapping}
            changedKeys={changedKeys}
            removedKeys={removedKeys}
          />

          <ChatMapping
            currentMapping={currentMapping}
            metadata={$appState.metadata}
            nexusDefinition={$appState.nexusResult!.nexus_definition}
            instrument={$appState.nexusResult!.detected_instrument}
            experiment={$appState.nexusResult!.detected_experiment}
            fileId={$appState.fileId || ''}
            model={selectedModel}
            onmappingUpdate={handleMappingUpdate}
          />
        </section>
      {/if}
    {/if}
  </main>

  <!-- Notifications -->
  <NotificationContainer />
</div>

<style>
  .app-container {
    @apply min-h-screen bg-gray-50;
  }

  .app-header {
    @apply bg-white shadow-sm border-b border-gray-200;
  }

  .header-content {
    @apply max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between;
  }

  .header-title {
    @apply text-center sm:text-left;
  }

  .title {
    @apply text-3xl font-bold text-gray-900 mb-2;
  }

  .subtitle {
    @apply text-lg text-gray-600;
  }

  .header-actions {
    @apply flex items-center gap-3;
  }

  .reset-button {
    @apply px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500;
  }

  .user-info {
    @apply flex items-center gap-3;
  }

  .user-name {
    @apply text-sm text-gray-600 hidden sm:inline;
  }

  .login-button {
    @apply px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2;
  }

  .logout-button {
    @apply px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed;
  }

  .loading-text {
    @apply text-sm text-gray-500;
  }

  .spinner-small {
    @apply animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full;
  }

  .spinner-large {
    @apply animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full;
  }

  .loading-section {
    @apply flex items-center justify-center py-16;
  }

  .loading-card {
    @apply flex flex-col items-center gap-4 text-gray-600;
  }

  .login-prompt-section {
    @apply flex items-center justify-center py-16;
  }

  .login-prompt-card {
    @apply bg-white rounded-lg shadow-md p-8 max-w-md text-center;
  }

  .login-prompt-icon {
    @apply text-5xl mb-4;
  }

  .login-prompt-title {
    @apply text-2xl font-bold text-gray-900 mb-2;
  }

  .login-prompt-message {
    @apply text-gray-600 mb-6;
  }

  .login-prompt-button {
    @apply w-full justify-center;
  }

  .app-main {
    @apply max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8;
  }

  .intro-section {
    @apply bg-white border border-gray-200 rounded-lg p-6 flex flex-col sm:flex-row items-center gap-6;
  }

  .intro-logos {
    @apply flex items-center gap-4 flex-shrink-0;
  }

  .intro-logo {
    @apply h-20 w-auto;
  }

  .intro-text {
    @apply text-center sm:text-left;
  }

  .intro-title {
    @apply text-xl font-bold text-gray-900 mb-2;
  }

  .intro-description {
    @apply text-gray-600 leading-relaxed;
  }

  .upload-section {
    @apply space-y-4;
  }

  .preview-section {
    @apply space-y-4;
  }

  .preview-container {
    @apply flex flex-col items-center space-y-4;
  }

  .preview-title {
    @apply text-xl font-semibold text-gray-900;
  }

  .upload-actions {
    @apply flex justify-center;
  }

  .primary-button {
    @apply px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2;
  }

  .secondary-button {
    @apply px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2;
  }

  .spinner {
    @apply animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full;
  }

  .error-section {
    @apply space-y-4;
  }

  .error-card {
    @apply bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3;
  }

  .error-icon {
    @apply text-2xl flex-shrink-0;
  }

  .error-content {
    @apply flex-1;
  }

  .error-title {
    @apply text-lg font-semibold text-red-900 mb-1;
  }

  .error-message {
    @apply text-red-800;
  }

  .metadata-section, .results-section {
    @apply space-y-4;
  }

  .nexus-inputs {
    @apply bg-white border border-gray-200 rounded-lg p-6 space-y-4;
  }

  .nexus-inputs-title {
    @apply text-lg font-semibold text-gray-900;
  }

  .nexus-inputs-grid {
    @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4;
  }

  .input-group {
    @apply flex flex-col gap-1;
  }

  .input-label {
    @apply text-sm font-medium text-gray-700;
  }

  .text-input {
    @apply px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500;
  }

  .select-input {
    @apply px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500;
  }

  .generate-action {
    @apply flex justify-end;
  }

  .section-header {
    @apply flex items-center justify-between mb-4;
  }

  .section-title {
    @apply text-2xl font-bold text-gray-900;
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    .header-content {
      @apply flex-col text-center space-y-4;
    }

    .section-header {
      @apply flex-col text-center space-y-2;
    }

    .upload-actions {
      @apply w-full;
    }

    .primary-button, .secondary-button {
      @apply w-full justify-center;
    }
  }
</style>